import React, { useState, useRef, useCallback, useEffect } from 'react';
import ColoringPage, { MIN_ZOOM, MAX_ZOOM } from './ColoringPage';
import Panel from './Panel';
import Palette, { PRESET_COLORS } from './Palette';
import Overview from './Overview';
import { exportPdf } from './pdf';
import { book } from './book';
import {
  FillIcon, PencilIcon, HandIcon, UndoIcon, RedoIcon, ChevronLeft, ChevronRight,
  GridIcon, DownloadIcon, MinusIcon, PlusIcon, EraserIcon, ResetIcon,
} from './icons';
import './App.css';

// Begge fargeleggingsbøkene ligger på ak-kreativ.no og deler dermed localStorage.
// Egen nøkkel per bok hindrer at den ene bokas arbeid dukker opp i den andre.
const STORAGE_PREFIX = `fargelegging-${book.id}:`;
const KEYS = {
  actions: `${STORAGE_PREFIX}actions`,
  answers: `${STORAGE_PREFIX}answers`,
  name: `${STORAGE_PREFIX}name`,
  customColors: `${STORAGE_PREFIX}customColors`,
};
const INITIAL_CUSTOM_COLORS = ['#FFFFFF', '#000000', '#C0C0C0', '#F78DA7', '#8E44AD', '#3498DB'];
const ZOOM_STEP = 1.25;

// Forsiden er side 0, deretter bokas sider
const PAGES = [{ imageUrl: book.cover.imageUrl, alt: `Forsiden til ${book.title}` }, ...book.pages];

const load = (key, fallback) => {
  try {
    const saved = localStorage.getItem(key);
    return saved !== null ? JSON.parse(saved) : fallback;
  } catch (e) {
    console.error('Kunne ikke laste', key, e);
    return fallback;
  }
};

const ToolButton = ({ active, onClick, icon, label, title }) => (
  <button className={`tool-button${active ? ' is-active' : ''}`} aria-pressed={active} onClick={onClick} title={title}>
    {icon}
    <span>{label}</span>
  </button>
);

function App() {
  const [currentPage, setCurrentPage] = useState(0);
  const [actions, setActions] = useState(() => load(KEYS.actions, {}));
  const [undoneActions, setUndoneActions] = useState({});
  const [answers, setAnswers] = useState(() => load(KEYS.answers, {}));
  const [name, setName] = useState(() => load(KEYS.name, ''));
  const [customColors, setCustomColors] = useState(() => load(KEYS.customColors, INITIAL_CUSTOM_COLORS));
  const [activeColor, setActiveColor] = useState(PRESET_COLORS[7][0]);
  const [selectedCustom, setSelectedCustom] = useState(null);
  const [activeTool, setActiveTool] = useState('fill');
  const [showBrush, setShowBrush] = useState(true);
  const [brushSize, setBrushSize] = useState(5);
  const [zoom, setZoom] = useState(1);
  const [exportProgress, setExportProgress] = useState(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [showOverview, setShowOverview] = useState(false);
  const coloringPageRef = useRef(null);

  const pageKey = `page_${currentPage}`;
  const pageActions = actions[pageKey] || [];
  const pageUndone = undoneActions[pageKey] || [];
  const canUndo = pageActions.length > 0;
  const canRedo = pageUndone.length > 0;
  const isExporting = exportProgress !== null;
  const lastPage = PAGES.length - 1;

  const persist = useCallback((key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      setSaveFailed(false);
    } catch (e) {
      console.error('Kunne ikke lagre', e);
      setSaveFailed(true);
    }
  }, []);

  const goToPage = useCallback((index) => {
    setCurrentPage(Math.max(0, Math.min(lastPage, index)));
    setZoom(1);
    setShowOverview(false);
  }, [lastPage]);

  const setPageActions = (newPageActions, newUndone) => {
    const newActions = { ...actions, [pageKey]: newPageActions };
    setActions(newActions);
    setUndoneActions((prev) => ({ ...prev, [pageKey]: newUndone }));
    persist(KEYS.actions, newActions);
  };

  const handleNewAction = (action) => {
    setPageActions([...pageActions, action], []);
    // Strektykkelse-vinduet skal ikke ligge over bildet mens eleven tegner
    if (action.type === 'draw' || action.type === 'erase') setShowBrush(false);
  };

  const handleUndo = () => {
    if (!canUndo) return;
    setPageActions(pageActions.slice(0, -1), [pageActions[pageActions.length - 1], ...pageUndone]);
  };

  // Fjerner all fargelegging på siden (svarene beholdes). Lagres som en
  // handling, så «Angre» henter alt tilbake.
  const handleClearPage = () => {
    if (!canUndo || pageActions[pageActions.length - 1].type === 'clear') return;
    if (!window.confirm('Vil du fjerne all fargelegging på denne siden?\n\nSvarene dine beholdes, og du kan trykke «Angre» for å få fargene tilbake.')) return;
    handleNewAction({ type: 'clear' });
  };

  const handleRedo = () => {
    if (!canRedo) return;
    setPageActions([...pageActions, pageUndone[0]], pageUndone.slice(1));
  };

  // Ctrl+Z / Ctrl+Y for fargeleggingen, men ikke mens eleven skriver
  const undoRef = useRef(handleUndo);
  const redoRef = useRef(handleRedo);
  undoRef.current = handleUndo;
  redoRef.current = handleRedo;
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = e.target.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); undoRef.current(); }
      else if (key === 'y' || (key === 'z' && e.shiftKey)) { e.preventDefault(); redoRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleAnswerChange = (index, value) => {
    const pageAnswers = [...(answers[pageKey] || [])];
    pageAnswers[index] = value;
    const newAnswers = { ...answers, [pageKey]: pageAnswers };
    setAnswers(newAnswers);
    persist(KEYS.answers, newAnswers);
  };

  const handleNameChange = (value) => {
    setName(value);
    persist(KEYS.name, value);
  };

  const isStrokeTool = (tool) => tool === 'pencil' || tool === 'eraser';
  const pickTool = (tool) => {
    if (isStrokeTool(tool) && tool === activeTool) setShowBrush(!showBrush);
    else if (isStrokeTool(tool)) setShowBrush(true);
    setActiveTool(tool);
  };

  const pickPreset = (hex) => {
    setActiveColor(hex);
    setSelectedCustom(null);
    if (activeTool === 'pan' || activeTool === 'eraser') setActiveTool('fill');
  };

  const pickCustom = (index) => {
    setActiveColor(customColors[index]);
    setSelectedCustom(index);
    if (activeTool === 'pan' || activeTool === 'eraser') setActiveTool('fill');
  };

  const editCustom = (index, hex) => {
    const next = [...customColors];
    next[index] = hex;
    setCustomColors(next);
    setActiveColor(hex);
    persist(KEYS.customColors, next);
  };

  const handleExport = async () => {
    setExportProgress(0);
    try {
      await exportPdf({ book, actions, answers, name, onProgress: (i) => setExportProgress(i) });
    } catch (error) {
      console.error('Feil under PDF-eksport:', error);
      alert('Det oppstod en feil under PDF-eksport. Vennligst prøv igjen.');
    } finally {
      setExportProgress(null);
    }
  };

  // 100 % betyr at hele bildet vises
  const zoomLabel = `${Math.round(zoom * 100)} %`;
  const pageLabel = currentPage === 0 ? 'Forside' : `Side ${currentPage} av ${lastPage}`;
  const currentData = PAGES[currentPage];

  return (
    <div className="app">
      <header className="topbar">
        <div className="book-title">{book.title}</div>
        <nav className="page-nav" aria-label="Sider">
          <button className="round-button" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 0} aria-label="Forrige side">
            <ChevronLeft size={20} />
          </button>
          <div className="page-position">
            <div className="page-label" aria-live="polite">{pageLabel}</div>
            <div className="progress-dots" aria-hidden="true">
              {PAGES.map((_, i) => (
                <span key={i} className={i === currentPage ? 'is-current' : i < currentPage ? 'is-before' : ''} />
              ))}
            </div>
          </div>
          <button className="round-button" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === lastPage} aria-label="Neste side">
            <ChevronRight size={20} />
          </button>
          <button className="text-button" onClick={() => setShowOverview(true)}>
            <GridIcon size={18} /> Alle sider
          </button>
        </nav>
        <div className="topbar-end">
          <button className="primary-button" onClick={handleExport} disabled={isExporting}>
            <DownloadIcon size={18} />
            {isExporting ? `Lager PDF (${exportProgress}/${PAGES.length}) …` : 'Lag PDF'}
          </button>
        </div>
      </header>

      <div className="workspace">
        <aside className="tool-rail" aria-label="Verktøy">
          <ToolButton active={activeTool === 'fill'} onClick={() => pickTool('fill')} icon={<FillIcon size={24} />}
            label="Fyll" title="Fyll et område med farge" />
          <ToolButton active={activeTool === 'pencil'} onClick={() => pickTool('pencil')} icon={<PencilIcon size={24} />}
            label="Blyant" title="Tegn frihånd" />
          <ToolButton active={activeTool === 'eraser'} onClick={() => pickTool('eraser')} icon={<EraserIcon size={24} />}
            label="Viskelær" title="Visk ut og få tilbake originaltegningen" />
          <ToolButton active={activeTool === 'pan'} onClick={() => pickTool('pan')} icon={<HandIcon size={24} />}
            label="Flytt" title="Flytt bildet (eller dra med to fingre)" />
          <div className="rail-divider" />
          <button className="rail-button" onClick={handleUndo} disabled={!canUndo} title="Angre (Ctrl+Z)">
            <UndoIcon /><span>Angre</span>
          </button>
          <button className="rail-button" onClick={handleRedo} disabled={!canRedo} title="Gjør om (Ctrl+Y)">
            <RedoIcon /><span>Gjør om</span>
          </button>
          <button className="rail-button" onClick={handleClearPage} disabled={!canUndo || pageActions[pageActions.length - 1].type === 'clear'}
            title="Fjern all fargelegging på denne siden">
            <ResetIcon /><span>Tøm siden</span>
          </button>
          <div className="rail-divider" />
          <div className="zoom-group" role="group" aria-label="Zoom">
            <div className="zoom-buttons">
              <button onClick={() => coloringPageRef.current?.zoomTo(zoom / ZOOM_STEP)} disabled={zoom <= MIN_ZOOM + 0.001} aria-label="Zoom ut">
                <MinusIcon size={18} />
              </button>
              <button onClick={() => coloringPageRef.current?.zoomTo(zoom * ZOOM_STEP)} disabled={zoom >= MAX_ZOOM - 0.001} aria-label="Zoom inn">
                <PlusIcon size={18} />
              </button>
            </div>
            <span className="zoom-label" aria-live="polite">{zoomLabel}</span>
            <button className="zoom-fit" onClick={() => coloringPageRef.current?.zoomToFit()} disabled={zoom <= MIN_ZOOM + 0.001}>
              Hele bildet
            </button>
          </div>
        </aside>

        {isStrokeTool(activeTool) && showBrush && (
          <div className="brush-popover" role="group" aria-label={activeTool === 'eraser' ? 'Størrelse på viskelæret' : 'Strektykkelse'}>
            <div className="brush-header">
              <label htmlFor="brush-size">{activeTool === 'eraser' ? 'Viskelærets størrelse' : 'Strektykkelse'}</label>
              <span>{brushSize}</span>
            </div>
            <div className="brush-preview">
              <span className={activeTool === 'eraser' ? 'is-eraser' : ''}
                style={{ width: brushSize, height: brushSize, background: activeTool === 'eraser' ? '#FFFFFF' : activeColor }} />
            </div>
            <input id="brush-size" type="range" min="1" max="50" value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))} />
          </div>
        )}

        <main className="canvas-column">
          <ColoringPage
            ref={coloringPageRef}
            key={currentPage}
            imageUrl={currentData.imageUrl}
            imageAlt={currentData.alt || `Side ${currentPage}: ${currentData.title || 'bilde til fargelegging'}`}
            actions={pageActions}
            onNewAction={handleNewAction}
            fillColor={activeColor}
            zoom={zoom}
            onZoomChange={setZoom}
            activeTool={activeTool}
            brushSize={brushSize}
          />
          <Palette
            activeColor={activeColor}
            customColors={customColors}
            selectedCustom={selectedCustom}
            onPickPreset={pickPreset}
            onPickCustom={pickCustom}
            onEditCustom={editCustom}
          />
        </main>

        <Panel
          book={book}
          pageIndex={currentPage}
          answers={answers[pageKey] || []}
          onAnswerChange={handleAnswerChange}
          name={name}
          onNameChange={handleNameChange}
          onStart={() => goToPage(1)}
          saveFailed={saveFailed}
        />
      </div>

      {showOverview && (
        <Overview
          book={book}
          pages={PAGES}
          currentPage={currentPage}
          actions={actions}
          answers={answers}
          name={name}
          onSelect={goToPage}
          onClose={() => setShowOverview(false)}
          onExport={handleExport}
          isExporting={isExporting}
        />
      )}
    </div>
  );
}

export default App;
